// Shared wizard-screen shell.
//
// * Progress bar (step X of 9)
// * Card body slot
// * Sticky "Back / Continue" footer on mobile; inline on desktop
// * "Start over" opens a confirm dialog before clearing the draft
// * Handles keyboard: Enter on any field submits Continue when valid

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../AppShell';
import { BrandButton } from '../BrandButton';
import { STEP_ROUTES, STEP_LABELS, resetDraft } from '../../lib/plannerDraft';

export function WizardScreen({
  step,
  title,
  eyebrow,
  children,
  onContinue,
  canContinue = true,
  onBack,
  hideBack = false,
  extraFooter = null,
  submitLabel = 'Continue',
}) {
  const navigate = useNavigate();
  const [showReset, setShowReset] = useState(false);

  const goBack = useCallback(() => {
    if (onBack) return onBack();
    if (step <= 1) return navigate('/');
    return navigate(STEP_ROUTES[step - 1]);
  }, [navigate, onBack, step]);

  const submit = useCallback(() => {
    if (onContinue) onContinue();
  }, [onContinue]);

  const handleKey = useCallback(
    (e) => {
      // Enter on non-textarea submits.
      const tag = e.target.tagName;
      if (e.key === 'Enter' && tag !== 'TEXTAREA') {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  const confirmReset = () => {
    setShowReset(false);
    // Clear the draft synchronously so no stale data survives even if
    // navigation is interrupted, then hard-nav to Welcome so any
    // still-mounted /plan/* guard is unmounted before the next render.
    resetDraft();
    window.location.assign('/');
  };

  const label = `Step ${step} of 9 · ${STEP_LABELS[step] || ''}`;

  return (
    <AppShell progress={{ total: 9, current: step, label }}>
      <div
        className="wizard-card"
        onKeyDown={handleKey}
        role="form"
        aria-labelledby="wizard-screen-title"
        data-testid={`wizard-step-${step}-card`}
      >
        {eyebrow && (
          <div className="eyebrow" data-testid={`wizard-step-${step}-eyebrow`}>
            {eyebrow}
          </div>
        )}
        <h1
          id="wizard-screen-title"
          className="h2 mt-3"
          data-testid={`wizard-step-${step}-title`}
        >
          {title}
        </h1>
        <div className="mt-6">{children}</div>

        {/* Desktop / tablet footer inside the card */}
        <div className="hidden md:flex items-center justify-between mt-10 pt-6" style={{ borderTop: '1px solid var(--line)' }}>
          <div className="flex items-center gap-3">
            {!hideBack && (
              <BrandButton
                variant="outline"
                size="sm"
                onClick={goBack}
                data-testid={`wizard-step-${step}-back-btn`}
              >
                Back
              </BrandButton>
            )}
            <button
              type="button"
              onClick={() => setShowReset(true)}
              className="text-sm underline underline-offset-4 opacity-60 hover:opacity-100"
              style={{ color: 'var(--ink)' }}
              data-testid={`wizard-step-${step}-reset-btn`}
            >
              Start over
            </button>
          </div>
          <div className="flex items-center gap-4">
            {extraFooter}
            <BrandButton
              variant="primary"
              size="md"
              onClick={submit}
              data-testid={`wizard-step-${step}-continue-btn`}
            >
              {submitLabel}
            </BrandButton>
          </div>
        </div>
      </div>

      {/* Mobile sticky footer */}
      <div className="md:hidden sticky-footer flex flex-col gap-2" data-testid={`wizard-step-${step}-mobile-footer`}>
        {extraFooter}
        <BrandButton
          variant="primary"
          size="lg"
          fullWidth
          onClick={submit}
          data-testid={`wizard-step-${step}-continue-btn-mobile`}
        >
          {submitLabel}
        </BrandButton>
        <div className="flex items-center justify-between px-1">
          {!hideBack ? (
            <button
              type="button"
              onClick={goBack}
              className="text-sm underline underline-offset-4"
              style={{ color: 'var(--ink)' }}
              data-testid={`wizard-step-${step}-back-btn-mobile`}
            >
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => setShowReset(true)}
            className="text-sm underline underline-offset-4 opacity-60"
            style={{ color: 'var(--ink)' }}
            data-testid={`wizard-step-${step}-reset-btn-mobile`}
          >
            Start over
          </button>
        </div>
      </div>

      {showReset && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-title"
          className="fixed inset-0 z-[80] flex items-center justify-center p-6"
          style={{ background: 'rgba(15,14,12,0.6)' }}
          data-testid="wizard-reset-confirm"
        >
          <div className="surface p-6 max-w-md w-full">
            <h2 id="reset-title" className="h3 mb-3">Start over?</h2>
            <p style={{ color: 'var(--text-dim)' }}>
              This will clear the plan you have built so far. This action
              can&apos;t be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <BrandButton
                variant="outline"
                size="sm"
                onClick={() => setShowReset(false)}
                data-testid="wizard-reset-cancel-btn"
              >
                Keep planning
              </BrandButton>
              <BrandButton
                variant="ink"
                size="sm"
                onClick={confirmReset}
                data-testid="wizard-reset-confirm-btn"
              >
                Start over
              </BrandButton>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

export default WizardScreen;
